import { useCallback, useEffect, useRef, useState } from "react";
import * as pmRepo from "@/stores/pm/repository";
import {
  protocolDataKey,
  protocolQueryBounds,
  type ProtocolDateRange,
} from "@/domains/pm/utils/protocol";
import type { ProtocolData, PMIssue } from "@/domains/pm/types";

export type ProtocolDataStatus = "idle" | "loading" | "ready" | "error";

export interface ProtocolDataState {
  status: ProtocolDataStatus;
  /** The data for `key`, or null while loading or errored. */
  data: ProtocolData | null;
  /** Identifies which period `data` belongs to. */
  key: string | null;
}

const IDLE: ProtocolDataState = { status: "idle", data: null, key: null };

/**
 * The protocol's period fetch, cached for the life of one open of the dialog.
 *
 * Deliberately imperative — `load()` is called from the Continue handler
 * rather than run from an effect keyed on the range. An effect's cleanup fires
 * when the user presses Back, which would abort a perfectly healthy in-flight
 * fetch; driving it from the click makes Back pure navigation, so the fetch
 * keeps running and Back → Continue costs nothing.
 *
 * The cache lives only as long as the mounted dialog (which unmounts on close),
 * preserving the existing "fresh on every open" guarantee: a period whose
 * issues moved while the dialog was shut is re-read.
 */
export function useProtocolData(params: { buildingId: number; buildingProjectId?: number }) {
  const { buildingId, buildingProjectId } = params;
  const [state, setState] = useState<ProtocolDataState>(IDLE);
  const cacheRef = useRef(new Map<string, ProtocolData>());
  const inFlightRef = useRef<{ key: string; controller: AbortController } | null>(null);

  // The latest state, readable from a callback that must not re-create itself
  // whenever the period changes. Written in an effect rather than during the
  // render: a ref mutated while rendering is torn by StrictMode's double
  // invocation and by an interrupted render, and `patchIssue` only ever reads
  // it from an event handler, which is long after the commit.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  /**
   * Replace one issue in the loaded period.
   *
   * BOTH the rendered state and the cache entry it came from, in step: `load()`
   * hands the cached object straight back, so patching state alone would let
   * Back → Continue resurrect the pre-edit row.
   *
   * A no-op when the issue is not in the period — an edit that moves an issue
   * out of the range is not this hook's business, and dropping the row would
   * silently un-include something the user had ticked.
   *
   * Deliberately not done inside the setState updater: StrictMode invokes
   * updaters twice, and a Map write has no business running in one.
   */
  const patchIssue = useCallback((issue: PMIssue) => {
    const { key, data } = stateRef.current;
    if (!key || !data) return;
    if (!data.issues.some((existing) => existing.id === issue.id)) return;
    const next: ProtocolData = {
      ...data,
      issues: data.issues.map((existing) =>
        existing.id === issue.id ? { ...existing, ...issue } : existing
      ),
    };
    cacheRef.current.set(key, next);
    setState((prev) => (prev.key === key ? { ...prev, data: next } : prev));
  }, []);

  const abort = useCallback(() => {
    inFlightRef.current?.controller.abort();
    inFlightRef.current = null;
  }, []);

  const load = useCallback(
    (range: ProtocolDateRange) => {
      const key = protocolDataKey(buildingId, range, buildingProjectId);

      const cached = cacheRef.current.get(key);
      if (cached) {
        // Cheapest path, but not a free one: a fetch for the period the user
        // just left may still be running, and its identity guard would pass —
        // replacing this ready state with the abandoned period's issues.
        abort();
        setState({ status: "ready", data: cached, key });
        return;
      }
      // Continue pressed twice, or Back → Continue while the first fetch is
      // still running: join it rather than restarting it.
      if (inFlightRef.current?.key === key) {
        setState((prev) => (prev.key === key ? prev : { status: "loading", data: null, key }));
        return;
      }
      // A fetch for a period the user has abandoned must not land.
      inFlightRef.current?.controller.abort();

      const controller = new AbortController();
      inFlightRef.current = { key, controller };
      setState({ status: "loading", data: null, key });

      void (async () => {
        try {
          const data = await pmRepo.getProtocolData(buildingId, {
            ...protocolQueryBounds(range),
            buildingProjectId,
            signal: controller.signal,
          });
          // Two guards, and both are load-bearing: abort() does not un-resolve
          // a promise that has already settled, so a continuation scheduled a
          // tick before the abort still runs. Only the identity check catches
          // an out-of-order pair of fetches.
          if (controller.signal.aborted || inFlightRef.current?.controller !== controller) return;
          cacheRef.current.set(key, data);
          inFlightRef.current = null;
          setState({ status: "ready", data, key });
        } catch (error) {
          if (controller.signal.aborted || inFlightRef.current?.controller !== controller) return;
          inFlightRef.current = null;
          // The toast-free branch: step 2 renders the failure with a Retry, so
          // the console is the only place a reported "it won't load" can be
          // diagnosed from.
          console.error("Protocol issue fetch failed", error);
          setState({ status: "error", data: null, key });
        }
      })();
    },
    [abort, buildingId, buildingProjectId]
  );

  return { state, load, abort, patchIssue };
}
